import React from 'react';
import { AlertCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { AuthSubmitButton } from './AuthSubmitButton';
import { GoogleAuthButton } from './GoogleAuthButton';
import { Button } from '@/components/ui/button';

interface ConflictResolutionViewProps {
    email: string;
    existingProvider: 'google' | 'email';
    attemptedProvider: 'google' | 'email';
    onSignInWithGoogle: () => Promise<void>;
    onGoBack: () => void;
    onGoToLogin?: () => void;
}

export const ConflictResolutionView: React.FC<ConflictResolutionViewProps> = ({
    email,
    existingProvider,
    attemptedProvider,
    onSignInWithGoogle,
    onGoBack,
    onGoToLogin
}) => {
    const t = useTranslations('auth.conflict');

    const getMessage = () => {
        if (existingProvider === 'google' && attemptedProvider === 'email') {
            return t('googleExisting', { email });
        }
        if (existingProvider === 'email' && attemptedProvider === 'google') {
            return t('emailExisting', { email });
        }
        if (existingProvider === 'email' && attemptedProvider === 'email') {
            return t('emailDuplicate', { email });
        }
        return t('genericConflict');
    };

    return (
        <div className="flex flex-col items-center text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="h-16 w-16 rounded-full bg-navy-subtle flex items-center justify-center mb-6 ring-1 ring-navy/25">
                <AlertCircle className="w-8 h-8 text-fg-secondary" />
            </div>

            <h2 className="text-sm font-semibold text-fg mb-3">{t('title')}</h2>

            <p className="text-sm text-fg-secondary leading-relaxed max-w-[280px] mb-8">
                {getMessage()}
            </p>

            <div className="w-full space-y-4 text-center mt-2">
                {existingProvider === 'google' && (
                    <GoogleAuthButton onClick={onSignInWithGoogle} />
                )}

                {existingProvider === 'email' && onGoToLogin && (
                    <AuthSubmitButton type="button" onClick={onGoToLogin}>
                        {t('logIn')}
                    </AuthSubmitButton>
                )}

                <Button
                    variant="ghost"
                    onClick={onGoBack}
                    className="w-full text-sm text-fg-muted hover:text-fg hover:bg-transparent transition-colors"
                >
                    {t('tryDifferentEmail')}
                </Button>
            </div>
        </div>
    );
};
